/**
 * Shared FixedPositionDropdownShell composition for the player-detail metric
 * dropdowns (SE-5568): the DotPlot X/Y axis selectors, the radar-chart
 * MetricSelector, the MetricDistributionRow per-row selector, and the
 * IndividualMetricInsights metric picker. Completes the SE-5551 migration of
 * the Performance Analytics surface off the gluestack Menu — every option
 * panel now renders through the shell, which owns trigger-anchored
 * measurement, portal + z-order, and viewport clamping (web).
 *
 * The option row preserves the pre-migration Menu look byte-for-byte:
 * label + bold value + right check, selected = accent-primary, unselected = theme text.
 * @remarks Pure rendering helpers — no hooks, state, or side effects
 */

import { Check } from "lucide-react-native";
import React from "react";

import {
  FixedPositionDropdownShell,
  FixedPositionDropdownShellBackdrop,
  FixedPositionDropdownShellPanel,
} from "@/components/atoms/FixedPositionDropdownShell";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import type { ShellDropdownWiring } from "@/features/player-detail/utils/performanceAnalyticsViewHelpers";

/**
 * Shared `useDropdownPositioning` params for the metric dropdowns. The 5px
 * offset matches the old Menu `offset={5}`; `panelHeight` caps long metric
 * lists behind an inner ScrollView and enables the bottom-edge viewport
 * clamp (both platforms — SE-5568 measures the trigger on native too).
 */
export const METRIC_DROPDOWN_POSITIONING = {
  panelMinWidth: 192,
  panelOffset: 5,
  panelHeight: 280,
} as const;

/**
 * One metric option row (label + value + selected check) inside a shell
 * panel — carries `accessibilityRole="menuitem"` like the gluestack MenuItem
 * it replaces.
 * @param params - Row params.
 * @param params.label - Metric label (React key + selection compare).
 * @param params.value - Metric value shown next to the label.
 * @param params.isSelected - Whether this option is the current selection.
 * @param params.onPress - Selection handler (must also close the panel).
 * @param params.isDark - Whether dark mode is active.
 * @returns The option Pressable.
 */
export const renderMetricShellOption = ({
  label,
  value,
  isSelected,
  onPress,
  isDark,
}: {
  readonly label: string;
  readonly value: number | null;
  readonly isSelected: boolean;
  readonly onPress: () => void;
  readonly isDark: boolean;
}): React.JSX.Element => (
  <Pressable
    key={label}
    accessibilityRole="menuitem"
    accessibilityLabel={label}
    accessibilityState={{ selected: isSelected }}
    onPress={onPress}
    className="min-w-0 px-3 py-2"
  >
    <Stack direction="horizontal" className="items-center gap-2">
      <Text
        variant={isSelected ? "body-strong" : "body"}
        className={
          isSelected
            ? "text-accent-primary"
            : isDark
              ? "text-white"
              : "text-content-secondary"
        }
      >
        {label}
      </Text>
      <Text
        variant="body-bold"
        className={
          isSelected
            ? "text-accent-primary"
            : isDark
              ? "text-white"
              : "text-content-muted"
        }
      >
        {value != null ? value : "--"}
      </Text>
      {isSelected ? (
        <Icon as={Check} size="xs" className="text-accent-primary" />
      ) : null}
    </Stack>
  </Pressable>
);

/**
 * Compose a trigger + shell-anchored option panel for one metric dropdown —
 * the SE-5551 `renderShellDropdown` shape, shared by every metric selector.
 * @param params - Dropdown params.
 * @param params.wiring - Open-state + positioning from the owning Container.
 * @param params.trigger - The shell-hosted trigger element.
 * @param params.options - Option rows to render.
 * @param params.panelClassName - Panel classes (defaults to the SE-5551 panel).
 * @returns The FixedPositionDropdownShell tree.
 */
export const renderMetricShellDropdown = ({
  wiring,
  trigger,
  options,
  panelClassName = "max-w-72 overflow-hidden py-1",
}: {
  readonly wiring: ShellDropdownWiring;
  readonly trigger: React.JSX.Element;
  readonly options: readonly React.JSX.Element[];
  readonly panelClassName?: string;
}): React.JSX.Element => (
  <FixedPositionDropdownShell>
    {trigger}
    {wiring.shouldShow ? (
      <>
        <FixedPositionDropdownShellBackdrop
          onPress={wiring.onToggle}
          accessibilityLabel="Close dropdown"
        />
        <FixedPositionDropdownShellPanel
          geometry={wiring.panelGeometry}
          onDismiss={wiring.onToggle}
          className={panelClassName}
        >
          <ScrollView>{options}</ScrollView>
        </FixedPositionDropdownShellPanel>
      </>
    ) : null}
  </FixedPositionDropdownShell>
);
